// components/LiveTicker.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Stack, Chip, alpha, Skeleton, Tooltip } from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  Diamond,
} from '@mui/icons-material';

const LiveTicker = () => {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const tickerRef = useRef(null);
  const animationRef = useRef(null);
  const [speed, setSpeed] = useState(0.8);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch from CoinGecko
  const fetchAllPrices = useCallback(async () => {
    try {
      const response = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=gold,silver,platinum,palladium&vs_currencies=usd'
      );
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      
      const data = await response.json();
      
      const timestamp = new Date();
      const priceItems = [];
      
      // Process Gold
      if (data.gold && data.gold.usd) {
        priceItems.push({
          id: 1,
          metal: 'GOLD',
          symbol: 'XAU',
          price: data.gold.usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          change: 0,
          changePercent: '+0.00%',
          trend: 'up',
          location: 'London',
        });
      }
      
      // Process Silver
      if (data.silver && data.silver.usd) {
        priceItems.push({
          id: 2,
          metal: 'SILVER',
          symbol: 'XAG',
          price: data.silver.usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          change: 0,
          changePercent: '+0.00%',
          trend: 'up',
          location: 'New York',
        });
      }
      
      // Process Platinum
      if (data.platinum && data.platinum.usd) {
        priceItems.push({
          id: 3,
          metal: 'PLATINUM',
          symbol: 'XPT',
          price: data.platinum.usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          change: 0,
          changePercent: '+0.00%',
          trend: 'up',
          location: 'Tokyo',
        });
      }
      
      // Process Palladium
      if (data.palladium && data.palladium.usd) {
        priceItems.push({
          id: 4,
          metal: 'PALLADIUM',
          symbol: 'XPD',
          price: data.palladium.usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          change: 0,
          changePercent: '+0.00%',
          trend: 'up',
          location: 'Zurich',
        });
      }
      
      if (priceItems.length === 0) {
        throw new Error('No price data received');
      }
      
      setPrices(priceItems);
      setLastUpdated(timestamp);
      setError(false);
      setLoading(false);
      
    } catch (error) {
      console.error('Error fetching prices:', error);
      setError(true);
      setLoading(false);
      setPrices([]);
    }
  }, []);

  // Fetch every 60 seconds
  useEffect(() => {
    fetchAllPrices();
    const interval = setInterval(fetchAllPrices, 60000);
    return () => clearInterval(interval);
  }, [fetchAllPrices]);

  const tickerItems = prices.length > 0 ? [...prices, ...prices, ...prices] : [];

  const animate = useCallback(() => {
    if (!isHovered && tickerRef.current && tickerItems.length > 0) {
      setScrollPosition((prev) => {
        const newPosition = prev - speed;
        const maxScroll = tickerRef.current.scrollWidth / 3;
        if (Math.abs(newPosition) >= maxScroll) {
          return 0;
        }
        return newPosition;
      });
    }
    animationRef.current = requestAnimationFrame(animate);
  }, [isHovered, speed, tickerItems.length]);

  useEffect(() => {
    if (tickerItems.length > 0) {
      animationRef.current = requestAnimationFrame(animate);
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate, tickerItems.length]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    setSpeed(0);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setSpeed(0.8);
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? 
      <TrendingUp sx={{ fontSize: 12, color: '#4caf50' }} /> : 
      <TrendingDown sx={{ fontSize: 12, color: '#f44336' }} />;
  };

  const getMetalColor = (metal) => {
    switch(metal) {
      case 'GOLD': return '#FFD700';
      case 'SILVER': return '#C0C0C0';
      case 'PLATINUM': return '#E5E4E2';
      case 'PALLADIUM': return '#E8E8E8';
      default: return '#FFD700';
    }
  };

  // Loading state
  if (loading && prices.length === 0) {
    return (
      <Box sx={{ 
        bgcolor: '#000066', 
        py: 1.5, 
        px: 2, 
        borderTop: '1px solid rgba(255, 215, 0, 0.2)', 
        borderBottom: '1px solid rgba(255, 215, 0, 0.2)',
        position: 'sticky',
        top: { xs: 60, md: 68 },
        zIndex: 1100,
      }}>
        <Stack direction="row" spacing={2}>
          <Skeleton variant="rectangular" width={220} height={40} sx={{ bgcolor: alpha('#FFD700', 0.1), borderRadius: 2 }} />
          <Skeleton variant="rectangular" width={220} height={40} sx={{ bgcolor: alpha('#FFD700', 0.1), borderRadius: 2 }} />
          <Skeleton variant="rectangular" width={220} height={40} sx={{ bgcolor: alpha('#FFD700', 0.1), borderRadius: 2 }} />
          <Skeleton variant="rectangular" width={220} height={40} sx={{ bgcolor: alpha('#FFD700', 0.1), borderRadius: 2 }} />
        </Stack>
      </Box>
    );
  }

  // Error state
  if (error && prices.length === 0) {
    return (
      <Box sx={{ 
        bgcolor: '#000066', 
        py: 1, 
        px: 2,
        textAlign: 'center',
        position: 'sticky',
        top: { xs: 60, md: 68 },
        zIndex: 1100,
      }}>
        <Typography variant="caption" sx={{ color: '#f44336' }}>
          ⚠️ Unable to load live prices. Please check your connection.
        </Typography>
      </Box>
    );
  }

  if (prices.length === 0 && !loading) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        top: { xs: 60, md: 68 },
        zIndex: 1100,
        bgcolor: '#000066',
        borderTop: '1px solid rgba(255, 215, 0, 0.25)',
        borderBottom: '1px solid rgba(255, 215, 0, 0.25)',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, #FFD700, #FFD700, transparent)',
          zIndex: 1,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.5), rgba(255, 215, 0, 0.5), transparent)',
          zIndex: 1,
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 100,
          background: 'linear-gradient(90deg, #000066 0%, transparent 100%)',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 100,
          background: 'linear-gradient(270deg, #000066 0%, transparent 100%)',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      <Box
        ref={tickerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 3,
          py: 1.2,
          pl: 50,
          pr: 50,
          transform: `translateX(${scrollPosition}px)`,
          whiteSpace: 'nowrap',
          cursor: isHovered ? 'pointer' : 'default',
        }}
      >
        {tickerItems.map((item, index) => (
          <Box
            key={`${item.id}-${index}`}
            sx={{
              display: 'inline-flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
              bgcolor: alpha('#FFFFFF', 0.03),
              px: 2.5,
              py: 0.9,
              borderRadius: 3,
              border: '1px solid',
              borderColor: alpha(getMetalColor(item.metal), 0.25),
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: getMetalColor(item.metal),
                bgcolor: alpha(getMetalColor(item.metal), 0.08),
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 16px ${alpha(getMetalColor(item.metal), 0.2)}`,
              },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(getMetalColor(item.metal), 0.25)} 0%, transparent 70%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Diamond sx={{ fontSize: 14, color: getMetalColor(item.metal) }} />
            </Box>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                color: getMetalColor(item.metal),
                letterSpacing: 0.8,
                fontSize: '0.85rem',
              }}
            >
              {item.metal}
            </Typography>

            <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.4), fontSize: '0.65rem', fontFamily: 'monospace', fontWeight: 500 }}>
              {item.symbol}
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 700, color: '#FFFFFF', fontFamily: 'monospace', fontSize: '0.85rem' }}>
              ${item.price}
            </Typography>

            <Stack direction="row" spacing={0.5} alignItems="center">
              {getTrendIcon(item.trend)}
              <Typography variant="caption" sx={{ color: item.trend === 'up' ? '#4caf50' : '#f44336', fontWeight: 700, fontSize: '0.7rem' }}>
                {item.changePercent}
              </Typography>
            </Stack>

            <Chip
              label={item.location}
              size="small"
              sx={{
                bgcolor: alpha('#FFD700', 0.12),
                color: '#FFD700',
                fontSize: '0.6rem',
                height: 22,
                fontWeight: 600,
                '& .MuiChip-label': { px: 1.2, fontSize: '0.6rem', fontWeight: 500 },
              }}
            />
          </Box>
        ))}
      </Box>

      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.3; transform: scale(1.2); }
          }
        `}
      </style>
    </Box>
  );
};

export default LiveTicker;